import { useEffect, useMemo, useRef, useState } from "react";
import { MenuButton } from "@fluentui/react-components";
import { IBreadcrumbItem, Icon, Stack, Text, TooltipHost, Callout, DirectionalHint } from "@fluentui/react";
import { useTranslation } from "react-i18next";
import { TooltipHostCalloutProps, topCalloutProps } from "../TooltipHostCalloutProps";
import Divider from "../Separator";
import SearchBox from "../SearchBox";
import { headerAIDSeachBoxProps } from "@/utilities/searchProps";
import { menuStyles } from "./styles/menuButtonStyles";
import { textStyles } from "@/styles/textStyles";
import { classNames, getCurrentCustomer, getCurrentInfo, getCurrentValue, getParameter, getPathName, getQueryVariable, removeLocalStorage, clearTimezoneCache } from "@/utilities/common";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/api";
import eventBus from "@/utilities/eventBus";
import styles from "./styles/menuButton.modules.less";
import { getBasePath, getDefaultRouter, isACustomerAgency } from "@/utilities/function";
import { isLeadGenAgency } from "@LeadGen/utilities/function";
import { useRouterConfig } from "@/routes/routerMap";
import { useAccountSwitch } from "@/hooks/useAccountSwitch";

interface TriggerMenuButtonProps {
  className: string,
  menuList: any
}

const TriggerMenuButton = (props: TriggerMenuButtonProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { switchAccount } = useAccountSwitch();
  const pathName = getPathName();
  const [visible, setVisible] = useState(false);
  const [accounts, setAccounts] = useState<any>();
  const [searchValue, setSearchValue] = useState<any>();
  const [currentAccount, setCurrentAccount] = useState<any>();
  const AID: any = Number(getParameter("aid"));
  const CID: any = Number(getParameter("cid"));
  const [clickCID, setClickCID] = useState<any>(false);
  const [checkedCustomerValues, setCheckedCustomerValues] = useState<Record<string, string[]>>({ customer: [String(CID)] });
  const [checkedAccountValues, setCheckedAccountValues] = useState<Record<string, string[]>>({ account: [String(AID)] });
  const btnRef = useRef<any>();
  const popoverRef = useRef<HTMLDivElement>(null);
  const searchBoxRef = useRef<HTMLDivElement>(null);
  const accountRadioRefs = useRef<Array<HTMLInputElement | null>>([]);
  const liveRegionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [accountBreadcrumb, setAccountBreadcrumb] = useState<any>([]);
  const [defaultItems, setDefaultItems] = useState<any>([]);
  const [liveMessage, setLiveMessage] = useState("");
  const { search } = useLocation();
  const routes = useRouterConfig();

  useEffect(() => {
    if (!visible) {
      return;
    }

    const timer = setTimeout(() => {
      const searchInput = searchBoxRef.current?.querySelector("input") as HTMLInputElement | null;
      searchInput?.focus();
    });

    return () => clearTimeout(timer);
  }, [visible]);

  useEffect(() => {
    if (!visible) {
      setLiveMessage("");
      if (liveRegionTimerRef.current) {
        clearTimeout(liveRegionTimerRef.current);
        liveRegionTimerRef.current = null;
      }
      return;
    }

    const normalizedSearchValue = typeof searchValue === "string" ? searchValue.trim() : "";
    if (!normalizedSearchValue) {
      setLiveMessage("");
      if (liveRegionTimerRef.current) {
        clearTimeout(liveRegionTimerRef.current);
        liveRegionTimerRef.current = null;
      }
      return;
    }

    const resultCount = Array.isArray(accounts) ? accounts.length : 0;
    const nextMessage = resultCount > 0
      ? t("Basics.searchResultsCount", { count: resultCount })
      : t("Basics.NoSearchResults");

    setLiveMessage("");
    if (liveRegionTimerRef.current) {
      clearTimeout(liveRegionTimerRef.current);
    }

    liveRegionTimerRef.current = setTimeout(() => {
      setLiveMessage(nextMessage);
      liveRegionTimerRef.current = null;
    }, 100);
  }, [visible, searchValue, accounts, t]);

  useEffect(() => {
    return () => {
      if (liveRegionTimerRef.current) {
        clearTimeout(liveRegionTimerRef.current);
      }
    };
  }, []);

  const handlePopoverKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      setVisible(false);
      btnRef.current?.focus();
      return;
    }

    if (e.key !== "Tab") {
      return;
    }

    const container = popoverRef.current;
    if (!container) {
      return;
    }

    const focusableItems = Array.from(
      container.querySelectorAll<HTMLElement>("input, button")
    ).filter((item) => item.offsetParent !== null && !item.hasAttribute("disabled") && item.getAttribute("aria-disabled") !== "true");

    if (focusableItems.length === 0) {
      return;
    }

    const currentIndex = focusableItems.indexOf(document.activeElement as HTMLElement);
    const nextIndex = e.shiftKey
      ? (currentIndex <= 0 ? focusableItems.length - 1 : currentIndex - 1)
      : (currentIndex === -1 || currentIndex === focusableItems.length - 1 ? 0 : currentIndex + 1);

    e.preventDefault();
    focusableItems[nextIndex]?.focus();
  };

  const calcPathName = useMemo(() => {
    const findChild = (datas: any, path: string) => {
      let result = null;
      datas?.forEach((item: any) => {
        if (item.path === path || `/${item.path}` === path || item.pathReg?.test(path)) {
          result = item;
        } else if (item.children && item.children?.length > 0) {
          const pathArr = path.split("/").slice(1);
          const curPath = pathArr.shift();
          if (item.path === `/${curPath}`) {
            result = findChild(item.children, `/${pathArr.join("/")}`);
          }
        }
      });
      return result;
    };
    const findItem: any = findChild(routes[0].children, pathName);
    return findItem?.fromPath || "";
  }, [pathName]);

  const handleAccountSwitch = async (accountId: number) => {
    const currentCustomerInfo = getCurrentCustomer(Number(CID))[0];
    const newBasePath = getBasePath(Number(currentCustomerInfo?.productType),
      Number(currentCustomerInfo?.primaryPurpose),
      calcPathName || pathName, currentCustomerInfo.region.toLowerCase() === "zh-cn" ? "/dashboard" : "/Non_dashboard");
    const url = getDefaultRouter(currentCustomerInfo?.customerType, `${isACustomerAgency(currentCustomerInfo?.customerType) &&
      !isLeadGenAgency(currentCustomerInfo?.agencyType)
      ? "AccountsManagement"
      : calcPathName.slice(1) || pathName.slice(1)}`, `?aid=${accountId}&cid=${CID}${search.indexOf("&t=") !== -1
        ? "&t=" + getQueryVariable("t")
        : ""}`, newBasePath.substring(1));

    setCheckedAccountValues((s) => ({ ...s, account: [String(accountId)] }));
    setVisible(false);

    await switchAccount({
      aid: accountId,
      cid: CID,
      url,
      accounts,
      suppressEvents: false
    });
  };

  const handleAccountRadioKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (!["ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight", "Home", "End", "Enter"].includes(e.key)) {
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();
      const target = e.currentTarget;
      const nextAccountId = Number(target.value);
      if (String(checkedAccountValues.account?.[0]) !== String(nextAccountId)) {
        handleAccountSwitch(nextAccountId);
      }
      return;
    }

    const radioItems = accountRadioRefs.current.filter(Boolean) as HTMLInputElement[];
    if (radioItems.length === 0) {
      return;
    }

    let nextIndex = index;
    if (e.key === "ArrowDown" || e.key === "ArrowRight") {
      nextIndex = (index + 1) % radioItems.length;
    } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
      nextIndex = (index - 1 + radioItems.length) % radioItems.length;
    } else if (e.key === "Home") {
      nextIndex = 0;
    } else if (e.key === "End") {
      nextIndex = radioItems.length - 1;
    }

    e.preventDefault();
    radioItems[nextIndex]?.focus();
  };
  useEffect(() => {
    setCheckedAccountValues((s) => ({ ...s, "account": [String(AID)] }));
    setCheckedCustomerValues((s) => ({ ...s, "customer": [String(CID)] }));
    if (props.menuList !== undefined) {
      const currentAccount = props.menuList.accounts !== undefined && props.menuList.accounts.length > 1
        ? props.menuList.accounts.find((val: any) => val.accountId === AID)
        : "";
      setCurrentAccount(currentAccount);
      setAccounts(props.menuList.accounts);
      const items = [
        {
          text: props.menuList.customerName + `${isACustomerAgency(getCurrentCustomer(Number(CID))[0]?.customerType) ? t("Basics.menuButton.billingAgency") : ""}`,
          key: props.menuList?.customerId,
        }
      ];
      setDefaultItems(items);
      setAccountBreadcrumb(items);
    }
    if (AID && AID !== 0) {
      api.Customer.customerSelect({ cid: Number(CID) }).then((response: any) => {
        api.Account.getAccount({ aid: AID }).then((res: any) => {
          if (res) {
            if (res?.accountStatus === 2) {
              setAccounts(props.menuList.accounts);
              navigate(`/errorPage?aid=${AID}${CID ? `&cid=${CID}` : ""}${search.indexOf("&t=") !== -1
                ? "&t=" + getQueryVariable("t")
                : ""}`);
            } else {
              if (pathName === "/errorPage") {
                const currentCustomerInfo = getCurrentCustomer(Number(CID))[0];
                const newBasePath = getBasePath(Number(currentCustomerInfo?.productType), Number(currentCustomerInfo?.primaryPurpose));
                const url = getDefaultRouter(currentCustomerInfo?.customerType,
                  !isLeadGenAgency(currentCustomerInfo?.agencyType)
                    ? "waitingOrder"
                    : "leadgen_accountManagement",
                  `?aid=${AID}${CID ? `&cid=${CID}` : ""}${search.indexOf("&t=") !== -1
                    ? "&t=" + getQueryVariable("t")
                    : ""}`, newBasePath);
                navigate(url);
              }
            }
            eventBus.emit("accountStatus", { accountStatus: res?.accountStatus });
            setCurrentAccount({ ...res });
          }
        });
      });
    }
    return () => {
      setCurrentAccount({});
    };
  }, [props.menuList, AID, pathName]);
  useEffect(() => {
    if (currentAccount) {
      const accountBreadcrumbItem = {
        text: currentAccount.accountName + `${currentAccount?.accountStatus === 2 ? t("Basics.menuButton.accountDisabled") : ""}`,
        key: currentAccount.accountId,
        onRender: () =>
          <Text
            block
            nowrap
            className={textStyles.textColorGray160}
          >
            {currentAccount !== undefined
              ? currentAccount?.accountName +
              `${currentAccount?.accountStatus === 2 ? t("Basics.menuButton.accountDisabled") : ""}`
              : ""}
          </Text>,
        onClick: (ev: any, item?: IBreadcrumbItem) => {
          console.log(`Breadcrumb item with key "${item?.key}" has been clicked.`);
        }
      };
      setAccountBreadcrumb([...defaultItems, accountBreadcrumbItem]);
    } else {
      setAccountBreadcrumb(defaultItems);
    }
  }, [currentAccount]);

  useEffect(() => {
    eventBus.off("createAccount");
    eventBus.on("createAccount", ({ newAid }) => {
      setCheckedAccountValues((s) => ({
        ...s,
        account: [String(newAid)]
      }));
    });
  }, []);

  // 新增：页面加载完成后的事件发射机制
  useEffect(() => {
    // 在组件挂载和AID/CID变化时，延迟发射数据更新事件
    if (AID && CID && accounts && accounts.length > 0) {
      const timer = setTimeout(() => {
        // 清除时区缓存，确保获取新客户的时区配置
        clearTimezoneCache();

        const currentCustomerInfo = getCurrentCustomer(Number(CID))[0];
        const currentAccountInfo = accounts?.find((acc: any) => acc.accountId === AID);

        // 只有当找到对应的账户信息时才发射事件，避免页面刷新时的不必要跳转
        if (currentAccountInfo && currentCustomerInfo) {
          // 发射新的数据更新事件，使用不同的事件名称避免与账户切换冲突
          eventBus.emit("accountDataLoaded", {
            aid: AID,
            cid: CID,
            customer: currentCustomerInfo,
            account: currentAccountInfo,
            isPageRefresh: true // 标识这是页面刷新触发的事件
          });
        }
      }, 500); // 给页面足够时间完成路由跳转

      return () => clearTimeout(timer);
    }
  }, [AID, CID, accounts]);

  return (
    <div className={menuStyles.wrapper}>
      {props.menuList !== undefined && JSON.stringify(props.menuList) !== "{}"
        ? <>
          <MenuButton ref={btnRef} className={menuStyles.longText} onClick={() => {
            setVisible(!visible);
          }}>
            <Text nowrap className={menuStyles.menuButonContent}>
              <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="center">
                <Text style={{ padding: "2px 4px 2px 4px", background: "#484644", borderRadius: 4 }}>{AID ? t("Basics.account") : t("Basics.customer")}</Text>
                <Text block nowrap style={{
                  wordWrap: "break-word",
                  maxWidth: 200
                }}>{AID ? getCurrentValue(currentAccount, "accountNumber") : props.menuList.customerNumber}</Text>
              </Stack>
              <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="center">
                <TooltipHost
                  content={
                    AID
                      ? getCurrentValue(currentAccount, "accountName")
                      : props.menuList.customerName +
                      `${isACustomerAgency(getCurrentCustomer(Number(CID))[0]?.customerType) ? t("Basics.menuButton.billingAgency") : ""}`}
                  calloutProps={TooltipHostCalloutProps}
                  id={"tooltip-accountName"}
                >
                  <Text aria-describedby={"tooltip-accountName"} block nowrap
                    style={{
                      wordWrap: "break-word",
                      maxWidth: 200
                    }}>{AID
                      ? getCurrentValue(currentAccount, "accountName") +
                      `${Number(getCurrentValue(currentAccount, "accountStatus")) === 2
                        ? t("Basics.menuButton.accountDisabled")
                        : ""}`
                      : isACustomerAgency(getCurrentCustomer(Number(CID))[0]?.customerType) &&
                        !isLeadGenAgency(getCurrentCustomer(Number(CID))[0]?.agencyType)
                        ? <Stack style={{ width: "100%" }} horizontal>
                          <Text block nowrap style={{
                            wordBreak: "break-all",
                            wordWrap: "break-word",
                            maxWidth: "60%",
                            minWidth: "20%",
                          }}>
                            {props.menuList.customerName}
                          </Text>
                          <span className={textStyles.textDisabled}>{t("Basics.menuButton.billingAgency")}</span>
                        </Stack>
                        : props.menuList.customerName}
                  </Text>
                </TooltipHost>
              </Stack>
            </Text>
          </MenuButton>
          {visible &&
            <Callout
              className={menuStyles.menuPopover}
              style={{ background: "#FAF9F8" }}
              gapSpace={0}
              doNotLayer={false}
              target={btnRef}
              directionalHint={DirectionalHint.bottomAutoEdge}
              directionalHintFixed={true}
              onDismiss={() => {
                setVisible(false);
                btnRef.current?.focus();
              }}
            >
              <div ref={popoverRef} onKeyDown={handlePopoverKeyDown}>
                <Text
                  aria-live="polite"
                  role="status"
                  style={{
                    position: "absolute",
                    width: 1,
                    height: 1,
                    padding: 0,
                    margin: -1,
                    overflow: "hidden",
                    clip: "rect(0, 0, 0, 0)",
                    whiteSpace: "nowrap",
                    border: 0,
                  }}
                >
                  {liveMessage}
                </Text>
                <TooltipHost
                  content={accountBreadcrumb.map((item: any) => item.text).join(" / ")}
                  calloutProps={topCalloutProps}
                >
                  <Text className={styles.menu_title} style={{
                    wordWrap: "break-word"
                  }}>
                    <span className={styles.title_cid_name}>
                      {accountBreadcrumb[0]?.text}
                    </span>
                    {accountBreadcrumb.length > 1
                      ? <>
                        <Icon iconName="ChevronRight" style={{ margin: "0 15px 0 8px" }} />
                        <span className={styles.title_aid_name}>{accountBreadcrumb[1]?.text}</span>
                      </>
                      : <></>
                    }
                  </Text>
                </TooltipHost>
                <Divider />
                <div>
                  <div ref={searchBoxRef}>
                    <SearchBox item={headerAIDSeachBoxProps(setAccounts, setSearchValue, t)} />
                  </div>
                  <div>
                    {searchValue && accounts.length === 0
                      ? <>
                        <div style={{ flexDirection: "column" }}>
                          <Text className={menuStyles.Text} block>{t("Basics.SearchResults")}</Text>
                          <div className={styles.no_data}>
                            <div className={menuStyles.MenuItemRadio}>
                              <Text className={textStyles.textColorGray160} block>{t("Basics.NoSearchResults")}</Text>
                            </div>
                          </div>
                        </div>
                      </>
                      : <>
                        <div className="customerItem">
                          <Text as="h2" className={menuStyles.Text}>{searchValue ? t("Basics.SearchResults") : t("Basics.menuButton.allAccounts")}</Text>
                          <button
                            type="button"
                            className={classNames({
                              [menuStyles.MenuItemRadio]: true,
                              [menuStyles.cidItemRadio]: true,
                              [menuStyles.menuRowButton]: true,
                            })}
                            aria-label={`${t("Basics.customer")} ${props.menuList.customerName} ${props.menuList.customerNumber}`}
                            onClick={() => {
                              clearTimezoneCache();
                              removeLocalStorage("MAPAccountStatus");
                              setClickCID(!clickCID);
                              if (clickCID) {
                                setCheckedCustomerValues((s) => ({ ...s, customer: [props.menuList?.customerId] }));
                              } else {
                                setCheckedCustomerValues((s) => ({ ...s, customer: [] }));
                              }
                              const newBasePath = getBasePath(Number(getCurrentCustomer(Number(CID))[0]?.productType), Number(getCurrentCustomer(Number(CID))[0]?.primaryPurpose), "leadgen_accountManagement",
                                getCurrentCustomer(Number(CID))[0]?.region.toLowerCase() === "zh-cn" ? "dashboard" : "Non_dashboard");
                              const url = getDefaultRouter(getCurrentInfo("customerType"),
                                !isLeadGenAgency(getCurrentInfo("agencyType"))
                                  ? "waitingOrder"
                                  : "leadgen_accountManagement", `?cid=${CID}`, newBasePath);
                              navigate(url);
                              eventBus.emit("accountChange", { cid: CID, isUserAction: true });
                              setVisible(false);
                            }}
                          >
                              <Stack horizontal horizontalAlign="space-between">
                                <Stack horizontal tokens={{ childrenGap: 10 }} className={menuStyles.customerTitleInfo}>
                                  <TooltipHost
                                    content={props.menuList.customerName +
                                      `${isACustomerAgency(getCurrentCustomer(Number(CID))[0]?.customerType)
                                        ? t("Basics.menuButton.billingAgency")
                                        : ""}`}
                                    calloutProps={TooltipHostCalloutProps}
                                  >
                                    <Text
                                      nowrap
                                      block
                                      style={{
                                        wordWrap: "break-word",
                                        maxWidth: 200
                                      }} className={textStyles.textColorGray160}>
                                      {isACustomerAgency(getCurrentCustomer(Number(CID))[0]?.customerType) &&
                                        !isLeadGenAgency(getCurrentCustomer(Number(CID))[0]?.agencyType)
                                        ? <Stack style={{ width: "100%" }} horizontal>
                                          <Text block nowrap style={{
                                            wordBreak: "break-all",
                                            wordWrap: "break-word",
                                            maxWidth: "60%",
                                            minWidth: "20%",
                                          }}>
                                            {props.menuList.customerName}
                                          </Text>
                                          <span className={textStyles.textDisabled}>{t("Basics.menuButton.billingAgency")}</span>
                                        </Stack>
                                        : props.menuList.customerName}
                                    </Text>
                                  </TooltipHost>
                                  <TooltipHost
                                    content={props.menuList.customerNumber}
                                    calloutProps={TooltipHostCalloutProps}
                                  >
                                    <Text block nowrap className={textStyles.textGray160}>{props.menuList.customerNumber}</Text>
                                  </TooltipHost>
                                </Stack>
                                <Stack className={styles.title_icon_box}>
                                  <Icon iconName="ChevronRight" />
                                </Stack>
                              </Stack>
                          </button>
                        </div>
                        <div className={menuStyles.accountItem}>
                          <div role="radiogroup" aria-label={t("Basics.menuButton.allAccounts")}>
                            {accounts && accounts.length > 0 && accounts.map((item: any, index: number) => {
                              const selectedAccountId = checkedAccountValues.account?.[0];
                              const isChecked = String(selectedAccountId) === String(item.accountId);
                              const isDisabledAccount = item?.accountStatus === 2 ||
                                (currentAccount?.accountStatus === 2 && item?.accountId === currentAccount?.accountId);
                              const accountNameText = `${item.accountName}${isDisabledAccount
                                ? ` ${t("Basics.menuButton.accountDisabled")}`
                                : ""}`;
                              const accountLabel = `${item.accountName}${item?.accountStatus === 2 ||
                                (currentAccount?.accountStatus === 2 && item?.accountId === currentAccount?.accountId)
                                ? ` ${t("Basics.menuButton.accountDisabled")}`
                                : ""} ${item.accountNumber}`;
                              const inputId = `account-switcher-${item.accountId}`;
                              return (<div
                                key={index}
                                className={classNames({
                                  [menuStyles.accountRow]: true,
                                  [menuStyles.selectedAccountRow]: isChecked,
                                })}
                              >
                                <input
                                  id={inputId}
                                  ref={(element) => {
                                    accountRadioRefs.current[index] = element;
                                  }}
                                  className={menuStyles.radioInput}
                                  type="radio"
                                  name="account-switcher"
                                  value={item.accountId}
                                  checked={isChecked}
                                  tabIndex={0}
                                  aria-label={accountLabel}
                                  onKeyDown={(e) => handleAccountRadioKeyDown(e, index)}
                                  onChange={() => {
                                    handleAccountSwitch(item.accountId);
                                  }}
                                />
                                <label htmlFor={inputId} className={menuStyles.menuRowLabel}>
                                <Stack horizontal tokens={{ childrenGap: 6 }} className={menuStyles.customerInfo}>
                                  <Text
                                    nowrap
                                    title={accountNameText}
                                    className={textStyles.textColorGray160}
                                    style={{
                                      wordWrap: "break-word"
                                    }}>
                                    {isDisabledAccount
                                      ? <Stack style={{ width: "100%" }} horizontal>
                                        <Text block nowrap style={{
                                          wordBreak: "break-all",
                                          wordWrap: "break-word",
                                          maxWidth: "70%",
                                          minWidth: "20%",
                                        }}>
                                          {item.accountName}
                                        </Text>
                                        <span className={textStyles.textDisabled}>{t("Basics.menuButton.accountDisabled")}</span>
                                      </Stack>
                                      : item.accountName}
                                  </Text>
                                  <Text
                                    nowrap
                                    title={item.accountNumber}
                                    className={textStyles.textGray160}
                                    style={{
                                      wordWrap: "break-word"
                                    }}
                                  >
                                    {item.accountNumber}
                                  </Text>
                                </Stack>
                                </label>
                              </div>);
                            })}

                          </div>
                        </div>
                      </>
                    }
                  </div>
                </div>
              </div>
            </Callout>
          }
        </>
        : null}
    </div>
  );
};

export default TriggerMenuButton;
